import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/apiClient';
import { FiltroCatalogo, NuevoProducto, Producto } from '../types/catalogo';

export function useProductos(filtro: FiltroCatalogo = {}) {
  const params = new URLSearchParams();
  if (filtro.categoriaId) params.set('categoriaId', filtro.categoriaId);
  if (filtro.texto) params.set('texto', filtro.texto);
  const query = params.toString();

  return useQuery({
    queryKey: ['productos', filtro],
    queryFn: () => apiFetch<Producto[]>(`/productos${query ? `?${query}` : ''}`),
  });
}

export function useProducto(id: string) {
  return useQuery({
    queryKey: ['productos', id],
    queryFn: () => apiFetch<Producto>(`/productos/${id}`),
    enabled: Boolean(id),
  });
}

export function useCrearProducto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (datos: NuevoProducto) =>
      apiFetch<Producto>('/productos', { method: 'POST', body: datos, autenticado: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['productos'] }),
  });
}

export function useActualizarProducto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cambios }: { id: string; cambios: Partial<NuevoProducto> }) =>
      apiFetch<Producto>(`/productos/${id}`, { method: 'PUT', body: cambios, autenticado: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['productos'] }),
  });
}

export function useEliminarProducto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/productos/${id}`, { method: 'DELETE', autenticado: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['productos'] }),
  });
}

export function useSubirImagen() {
  return useMutation({
    mutationFn: async ({ archivo }: { archivo: File }) => {
      const { urlSubida, urlPublica } = await apiFetch<{ urlSubida: string; urlPublica: string }>(
        '/imagenes/upload-url',
        {
          method: 'POST',
          body: { nombreArchivo: archivo.name, tipoContenido: archivo.type },
          autenticado: true,
        },
      );
      // Bug real corregido (2026-09-01): no se revisaba el resultado del PUT a S3 — si
      // fallaba (CSP, CORS, red), el error quedaba silencioso y el codigo seguia como si
      // la imagen se hubiera subido. fetch() solo rechaza la promesa por fallos de red;
      // un 4xx/5xx real de S3 resuelve igual y hay que chequear response.ok a mano.
      const respuesta = await fetch(urlSubida, {
        method: 'PUT',
        headers: { 'Content-Type': archivo.type },
        body: archivo,
      });
      if (!respuesta.ok) {
        throw new Error(`No se pudo subir "${archivo.name}" (${respuesta.status}). Intenta nuevamente.`);
      }
      return urlPublica;
    },
  });
}
